import { useEffect, useRef, useState } from 'react';
import { Undo2, CheckCircle2, Loader2, Fuel, ShoppingCart } from 'lucide-react';
import { OK, CHYBA, POZOR, hm, plocha, Tlacitko, Nadpis, tint, sestavTrasu, cinnostMista } from './ui';

/**
 * Zpět v bistru — shrnutí jízdy po „Ukončit rozvoz" (návrh obrazovek, 8).
 *
 * Dvě věci, které rozvozce u pultu řeší: kterou tašku vrátit (dvakrát
 * nezastižen) a jak jízda dopadla. Hotovost se tu neodevzdává — to patří
 * k vyúčtování směny (rozhodnuto 2026-10-12). „Hotovo" dávku potvrdí
 * (`cockpit-zavrit`) a vrátí ho mezi jízdy.
 *
 * Cockpit 2.0 (2026-10-19): když není co vracet, shrnutí se po
 * `ZAVRIT_SAMO_S` zavře samo — „Jsem zpět" je jediné ťuknutí po návratu.
 * Taška k vrácení (nezastižen) čeká na ťuk, ať ji nikdo nepřehlédne.
 */
const ZAVRIT_SAMO_S = 8;
/** Cíl: od objednání po doručení do hodiny. */
const CIL_MINUT = 60;

export default function Navrat({ data, onHotovo }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const odeslano = useRef(false);
    const davka = data.davka;
    const zastavky = data.zastavky ?? [];

    const doruceno = zastavky.filter(z => z.stav_doruceni === 'doruceno');
    const zpet = zastavky.filter(z => z.stav_doruceni === 'nezastizen');
    // Proti slibu i proti cíli 60 minut od objednání (2026-10-29) — dřív jen
    // „pozdě" nad toleranci, a i to se kvůli časové zóně nikdy neukázalo.
    const poSlibu = doruceno.filter(z => (z.rozdil_sec ?? 0) > 59);
    const nad60 = doruceno.filter(z => (z.minut_celkem ?? 0) > CIL_MINUT);
    const minut = davka.cas_prevzeti && davka.cas_navratu_skutecny
        ? Math.round((new Date(davka.cas_navratu_skutecny) - new Date(davka.cas_prevzeti)) / 60000)
        : null;

    const hotovo = async () => {
        if (odeslano.current) return;
        odeslano.current = true;
        setBusy(true);
        setErr(null);
        try {
            const r = await fetch(`/api/rozvoz/davky/${davka.id}/cockpit-zavrit`, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            onHotovo?.();
        } catch (e) {
            odeslano.current = false;
            setErr(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    const samo = zpet.length === 0;
    const [zbyva, setZbyva] = useState(ZAVRIT_SAMO_S);
    useEffect(() => {
        if (!samo || err) return undefined;
        const t = setInterval(() => setZbyva(s => s - 1), 1000);
        return () => clearInterval(t);
    }, [samo, err]);
    useEffect(() => {
        if (samo && zbyva <= 0 && !err) hotovo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zbyva]);

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface-base)', color: 'var(--text-primary)' }}>
            <header style={{ padding: 'calc(24px + env(safe-area-inset-top, 0px)) 20px 8px' }}>
                <Nadpis barva={OK}>Jízda hotová · {davka.kod}</Nadpis>
                <h1 style={{ margin: '4px 0 0', fontSize: 30, fontWeight: 900 }}>Zpět v bistru</h1>
                <p style={{ margin: '4px 0 0', fontSize: 15, color: 'var(--text-secondary)' }}>
                    Odjezd {hm(davka.cas_prevzeti)} · návrat {hm(davka.cas_navratu_skutecny)}{minut != null ? ` · ${minut} min` : ''}
                </p>
            </header>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
                    <Pocet cislo={doruceno.length} popis="doručeno" barva={OK} />
                    <Pocet cislo={poSlibu.length} popis="po slibu" barva={POZOR} />
                    <Pocet cislo={nad60.length} popis={`nad ${CIL_MINUT} min`} barva={CHYBA} />
                    <Pocet cislo={zpet.length} popis="zpět" barva={CHYBA} />
                </div>

                {zpet.length > 0 && (
                    <section style={{ ...plocha(), padding: 16, display: 'flex', alignItems: 'center', gap: 12, background: tint(CHYBA, 0.1), border: `1.5px solid ${tint(CHYBA, 0.5)}` }}>
                        <Undo2 size={26} style={{ color: CHYBA, flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                            <Nadpis barva={CHYBA}>Vrať do bistra</Nadpis>
                            {zpet.map(z => (
                                <div key={z.objednavka_id} style={{ fontSize: 17, fontWeight: 900 }}>
                                    #{z.cislo_denni ?? z.cislo_objednavky} · {z.adresa || '—'}
                                </div>
                            ))}
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>dvakrát nezastižen</div>
                        </div>
                    </section>
                )}

                {doruceno.length > 0 && (
                    <section style={{ ...plocha(), padding: '4px 14px' }}>
                        {/* Benzinky a obchody mezi doručenými, jak se jely (2026-10-29). */}
                        {sestavTrasu(doruceno, (data.mista ?? []).filter(m => m.stav === 'hotovo')).map((p, i) => (
                            <div key={p.klic} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, borderTop: i ? '1px solid var(--border-default)' : 'none' }}>
                                {p.typ === 'm' ? (<>
                                    {p.m.druh === 'benzinka' ? <Fuel size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} /> : <ShoppingCart size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />}
                                    <span style={{ flex: 1, fontSize: 15, fontWeight: 700, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-secondary)' }}>
                                        {p.m.nazev}
                                    </span>
                                    <PobytMista m={p.m} />
                                </>) : (<>
                                    <CheckCircle2 size={22} style={{ color: OK, flexShrink: 0 }} />
                                    <span style={{ flex: 1, fontSize: 15, fontWeight: 700, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        #{p.z.cislo_denni ?? p.z.cislo_objednavky} {p.z.adresa || ''}
                                    </span>
                                    <Vysledek z={p.z} />
                                </>)}
                            </div>
                        ))}
                    </section>
                )}

                {err && <div style={{ fontSize: 13, fontWeight: 700, color: CHYBA }}>{err}</div>}
            </div>

            <footer style={{ padding: '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))' }}>
                <Tlacitko plne barva={OK} onClick={hotovo} disabled={busy} vyska={64} style={{ width: '100%' }}>
                    {busy && <Loader2 size={18} className="animate-spin" />}
                    {samo && !err && zbyva > 0 ? `Hotovo · zavřu za ${zbyva} s` : 'Hotovo'}
                </Tlacitko>
            </footer>
        </div>
    );
}

/**
 * Dvě čísla u doručené zastávky: proti slibu („+9 min" / „včas") a celkem
 * od objednání proti cíli 60 minut. Barva: zelená v pořádku, oranžová
 * mírně přes, červená přes toleranci / nad hodinu.
 */
function Vysledek({ z }) {
    const rozdil = z.rozdil_sec != null ? Math.round(z.rozdil_sec / 60) : null;
    const celkem = z.minut_celkem;
    const barvaSlib = rozdil == null ? 'var(--text-muted)' : rozdil <= 0 ? OK : z.vysledek === 'ok' ? POZOR : CHYBA;
    const barvaCelkem = celkem == null ? 'var(--text-muted)' : celkem <= CIL_MINUT ? OK : CHYBA;
    return (
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, lineHeight: 1.15 }}>
            <span style={{ fontSize: 14, fontWeight: 900, color: barvaSlib, fontVariantNumeric: 'tabular-nums' }}>
                {rozdil == null ? 'bez slibu' : rozdil <= 0 ? `včas${rozdil < 0 ? ` · ${-rozdil} min dřív` : ''}` : `+${rozdil} min po slibu`}
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: barvaCelkem, fontVariantNumeric: 'tabular-nums' }}>
                {celkem == null ? '' : `${celkem} min od objednání`}
            </span>
        </span>
    );
}

/** Benzinka / obchod: kolik se počítalo a kolik to opravdu trvalo (příjezd podle GPS → Hotovo). */
function PobytMista({ m }) {
    const skutecne = m.prijezd_v && m.hotovo_v
        ? Math.max(1, Math.round((new Date(m.hotovo_v) - new Date(m.prijezd_v)) / 60000))
        : null;
    const barva = skutecne == null ? 'var(--text-muted)' : skutecne <= m.pobyt_min ? OK : skutecne <= m.pobyt_min + 3 ? POZOR : CHYBA;
    return (
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, lineHeight: 1.15 }}>
            <span style={{ fontSize: 14, fontWeight: 900, color: barva, fontVariantNumeric: 'tabular-nums' }}>
                {skutecne == null ? `hotovo ${hm(m.hotovo_v)}` : `${skutecne} min`}
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)' }}>
                {cinnostMista(m.druh)} · plán {m.pobyt_min} min
            </span>
        </span>
    );
}

function Pocet({ cislo, popis, barva }) {
    return (
        <div style={{ ...plocha({ zaobleni: 16 }), padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 30, fontWeight: 900, color: cislo > 0 ? barva : 'var(--text-muted)' }}>{cislo}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{popis}</div>
        </div>
    );
}
